import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import Timer from "../../components/Timer";
import CodingEditor from "../../components/CondingEditor";
// ✅ Correct file name

const QuestionsScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data = [], profileId, difficulty } = state || {};

  const [index, setIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [codingAnswer, setCodingAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  console.log("data", data);
  const current = data[index];

  // Timer countdown
  useEffect(() => {
    if (timeLeft === 0) {
      handleNext();
      return;
    }
    const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Next question logic
  const handleNext = () => {
    let newScore = score;
    if (
      current.type === "objective" &&
      selectedOption === current.correctAnswer
    ) {
      newScore += 1;
    } else if (current.type === "coding" && codingAnswer.trim().length > 0) {
      newScore += 0.5;
    }

    setScore(newScore);

    if (index + 1 < data.length) {
      setIndex(index + 1);
      setSelectedOption(null);
      setCodingAnswer("");
      setTimeLeft(60);
    } else {
      navigate(`/interview/${profileId}/result`, {
        state: { score: newScore, total: data.length, profileId, difficulty },
      });
    }
  };

  const handlePrev = () => {
    if (index > 0) {
      setIndex(index - 1);
      setSelectedOption(null);
      setCodingAnswer("");
      setTimeLeft(60);
    }
  };

  if (!data.length) {
    return (
      <EmptyContainer>
        <h2>No questions found. Please go back and try again.</h2>
      </EmptyContainer>
    );
  }

  return (
    <Wrapper>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Header>
          <div>
            <Title>
              {profileId?.toUpperCase()} Interview{" "}
              <LevelTag>{difficulty}</LevelTag>
            </Title>
            <ProgressBar>
              <ProgressFill
                style={{ width: `${((index + 1) / data.length) * 100}%` }}
              />
            </ProgressBar>
          </div>

          <RightHeader>
            <Timer timeLeft={timeLeft} />
            <ProgressText>
              {index + 1} / {data.length}
            </ProgressText>
          </RightHeader>
        </Header>
      </motion.div>

      <motion.div
        key={index}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card>
          <QuestionText>
            {index + 1}. {current.question}
          </QuestionText>

          {current.type === "objective" ? (
            <Options>
              {current.options?.map((opt, i) => (
                <OptionButton
                  key={i}
                  selected={selectedOption === opt}
                  onClick={() => setSelectedOption(opt)}
                >
                  {opt}
                </OptionButton>
              ))}
            </Options>
          ) : (
            <>
              <Label>Write your code below:</Label>
              <CodingEditor value={codingAnswer} onChange={setCodingAnswer} />
            </>
          )}

          <ButtonRow>
            <PrevButton
              disabled={index === 0}
              onClick={handlePrev}
              style={{ opacity: index === 0 ? 0.5 : 1 }}
            >
              ← Previous
            </PrevButton>
            <NextButton onClick={handleNext}>
              {index + 1 === data.length ? "Finish Interview" : "Next →"}
            </NextButton>
          </ButtonRow>
        </Card>
      </motion.div>
    </Wrapper>
  );
};

export default QuestionsScreen;

// ---------------- Styled Components ----------------

const Wrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px;
`;

const Header = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const LevelTag = styled.span`
  background: rgba(155, 92, 255, 0.15);
  color: #c59fff;
  border: 1px solid rgba(155, 92, 255, 0.4);
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-left: 10px;
`;

const ProgressBar = styled.div`
  width: 280px;
  height: 6px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  transition: width 0.4s ease;
`;

const RightHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const ProgressText = styled.span`
  font-size: 0.9rem;
  color: #c3c3c3;
  margin-top: 5px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 850px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 20px;
  padding: 40px 30px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 30px rgba(155, 92, 255, 0.2);
  transition: 0.3s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 420px; /* ✅ consistent height */
`;

const QuestionText = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #ffffff;
  line-height: 1.5;
  min-height: 70px; /* ✅ keeps space consistent for question text */
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const OptionButton = styled.button`
  background: ${(p) =>
    p.selected
      ? "linear-gradient(90deg, #9b5cff, #c59fff)"
      : "rgba(255, 255, 255, 0.05)"};
  color: ${(p) => (p.selected ? "#fff" : "#ccc")};
  border: 1px solid
    ${(p) => (p.selected ? "rgba(197,159,255,0.8)" : "rgba(155,92,255,0.25)")};
  border-radius: 12px;
  padding: 14px 18px;
  cursor: pointer;
  text-align: left;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.25s ease;

  &:hover {
    background: ${(p) =>
      p.selected
        ? "linear-gradient(90deg, #a46eff, #c59fff)"
        : "rgba(155,92,255,0.1)"};
    transform: translateY(-2px);
  }
`;

const Label = styled.p`
  color: #c59fff;
  font-size: 1rem;
  margin-bottom: 10px;
  font-weight: 500;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 35px;
  gap: 15px;
  flex-wrap: wrap;
`;

const BaseButton = styled.button`
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  padding: 14px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const NextButton = styled(BaseButton)`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.5);
  }
`;

const PrevButton = styled(BaseButton)`
  background: transparent;
  border: 1px solid rgba(155, 92, 255, 0.4);
  color: #c59fff;

  &:hover {
    background: rgba(155, 92, 255, 0.15);
    transform: translateY(-2px);
  }
`;

const EmptyContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #c59fff;
  text-align: center;
  padding: 60px;
  font-size: 1.2rem;
`;
